/**
 * Guided strategy-creation flow (Phase 1) — frontend-only contract between
 * /strategies/create (guided selection page) and /strategies/new (Strategy
 * Builder). Query values map to the exact existing builder state and saved
 * schema enums (strategy_source / external_source_type). Do not rename.
 *
 * Boundaries enforced here (single pure decision point, unit-tested):
 * - edit mode (/strategies/:id/edit) is never affected by guided params;
 * - admin for_user creation (/strategies/new?for_user=<id>) is never affected
 *   by guided params and never redirected;
 * - /strategies/new without valid guided params fails safe to /strategies/create.
 */

import { ROUTES } from '../../routes/constants';

// Guided query value -> builder/schema value
export const GUIDED_TYPE_MAP = {
  internal: 'INTERNAL',
  external: 'EXTERNAL',
  manual:   'MANUAL',
};

// Only providers that are active choices in the guided flow. CHARTINK exists
// in the builder enum but is Coming Soon in this flow, so it is not accepted.
export const GUIDED_PROVIDER_MAP = {
  tradingview: 'TRADINGVIEW',
  amibroker:   'AMIBROKER',
  python:      'PYTHON',
};

/**
 * Parse guided query params from a location.search string.
 * Returns { strategySource, externalSourceType } or null when missing/invalid.
 * - unknown/missing `type` -> null
 * - external requires provider in GUIDED_PROVIDER_MAP (chartink/unknown/missing -> null)
 * - provider is ignored for internal/manual
 */
export function parseGuidedParams(search) {
  const params = new URLSearchParams(search || '');
  const rawType = (params.get('type') || '').trim().toLowerCase();
  const strategySource = GUIDED_TYPE_MAP[rawType] || null;
  if (!strategySource) return null;
  if (strategySource !== 'EXTERNAL') {
    return { strategySource, externalSourceType: null };
  }
  const rawProvider = (params.get('provider') || '').trim().toLowerCase();
  const externalSourceType = GUIDED_PROVIDER_MAP[rawProvider] || null;
  if (!externalSourceType) return null;
  return { strategySource, externalSourceType };
}

/**
 * Build the builder URL for a guided selection. `type`/`provider` are the
 * guided (lowercase) keys. Fails safe to the guided page for invalid input.
 */
export function buildBuilderUrl({ type, provider } = {}) {
  const guidedType = (type || '').trim().toLowerCase();
  const strategySource = GUIDED_TYPE_MAP[guidedType];
  if (!strategySource) return ROUTES.STRATEGIES_CREATE;
  let url = `${ROUTES.STRATEGIES_NEW}?type=${guidedType}`;
  if (strategySource === 'EXTERNAL') {
    const guidedProvider = (provider || '').trim().toLowerCase();
    if (!GUIDED_PROVIDER_MAP[guidedProvider]) return ROUTES.STRATEGIES_CREATE;
    url += `&provider=${guidedProvider}`;
  }
  return url;
}

/**
 * Single decision point for how the Strategy Builder was entered. The builder
 * calls this once per render with values it already has; the result drives
 * initialization, locking, redirect, and back navigation.
 *
 * Returns exactly one of:
 *   { mode: 'edit' }    — /strategies/:id/edit. Guided params are ignored;
 *                         saved config hydration owns type/provider.
 *   { mode: 'admin' }   — /strategies/new?for_user=<id>. Guided params are
 *                         ignored; current editable selectors/banner unchanged.
 *   { mode: 'guided', strategySource, externalSourceType }
 *                       — valid guided creation; type/provider fixed.
 *   { mode: 'redirect' }— direct /strategies/new without valid guided params;
 *                         fail safe to /strategies/create.
 */
export function resolveBuilderEntry({ isEdit, forUserId, search }) {
  if (isEdit) return { mode: 'edit' };
  if (forUserId) return { mode: 'admin' };
  const guided = parseGuidedParams(search);
  if (!guided) return { mode: 'redirect' };
  return { mode: 'guided', ...guided };
}
